/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import type { SerializedStyles } from '@emotion/react';

import {
  ORG_SETTINGS_COLORS,
  ORG_SETTINGS_MEDIUM_ACTION_BUTTON_STYLE,
  ORG_SETTINGS_SPACING,
} from './OrgSettingsStyleLib';

export type ApiKeysTabContentStyles = {
  panel: SerializedStyles;
  content: SerializedStyles;
  introduction: SerializedStyles;
  listHeader: SerializedStyles;
  list: SerializedStyles;
  emptyState: SerializedStyles;
  machineUserCard: SerializedStyles;
  cardHeader: SerializedStyles;
  editForm: SerializedStyles;
  metadata: SerializedStyles;
  actions: SerializedStyles;
  credentialList: SerializedStyles;
  credential: SerializedStyles;
  credentialHeader: SerializedStyles;
  status: SerializedStyles;
  activeStatus: SerializedStyles;
  revokedStatus: SerializedStyles;
  secondaryAction: SerializedStyles;
  dangerAction: SerializedStyles;
};

export const DEFAULT_API_KEYS_TAB_CONTENT_STYLES: ApiKeysTabContentStyles = {
  panel: css`
    label: API_KEYS_TAB_CONTENT_PANEL;
    flex: 0 0 auto;
  `,
  content: css`
    label: API_KEYS_TAB_CONTENT_CONTENT;
    display: flex;
    flex-direction: column;
    gap: ${ORG_SETTINGS_SPACING.standardPadding}px;
    padding: ${ORG_SETTINGS_SPACING.standardPadding}px;
  `,
  introduction: css`
    label: API_KEYS_TAB_CONTENT_INTRODUCTION;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${ORG_SETTINGS_SPACING.gutterX}px;
  `,
  listHeader: css`
    label: API_KEYS_TAB_CONTENT_LIST_HEADER;
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: ${ORG_SETTINGS_SPACING.gutterX}px;
  `,
  list: css`
    label: API_KEYS_TAB_CONTENT_LIST;
    display: flex;
    flex-direction: column;
    gap: ${ORG_SETTINGS_SPACING.gutterX}px;
  `,
  emptyState: css`
    label: API_KEYS_TAB_CONTENT_EMPTY_STATE;
    padding: ${ORG_SETTINGS_SPACING.standardPadding}px;
    border: 1px solid ${ORG_SETTINGS_COLORS.border};
  `,
  machineUserCard: css`
    label: API_KEYS_TAB_CONTENT_MACHINE_USER_CARD;
    display: flex;
    flex-direction: column;
    gap: ${ORG_SETTINGS_SPACING.standardPadding}px;
    padding: ${ORG_SETTINGS_SPACING.standardPadding}px;
    border: 1px solid ${ORG_SETTINGS_COLORS.border};
  `,
  cardHeader: css`
    label: API_KEYS_TAB_CONTENT_CARD_HEADER;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: ${ORG_SETTINGS_SPACING.gutterX}px;
  `,
  editForm: css`
    label: API_KEYS_TAB_CONTENT_EDIT_FORM;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${ORG_SETTINGS_SPACING.gutterX}px;
  `,
  metadata: css`
    label: API_KEYS_TAB_CONTENT_METADATA;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: ${ORG_SETTINGS_SPACING.gutterX}px;
    padding-top: ${ORG_SETTINGS_SPACING.standardPadding}px;
    border-top: 1px solid ${ORG_SETTINGS_COLORS.border};
  `,
  actions: css`
    label: API_KEYS_TAB_CONTENT_ACTIONS;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${ORG_SETTINGS_SPACING.gutterX}px;
  `,
  credentialList: css`
    label: API_KEYS_TAB_CONTENT_CREDENTIAL_LIST;
    display: flex;
    flex-direction: column;
    gap: ${ORG_SETTINGS_SPACING.standardPadding}px;
    padding-top: ${ORG_SETTINGS_SPACING.standardPadding}px;
    border-top: 1px solid ${ORG_SETTINGS_COLORS.border};
  `,
  credential: css`
    label: API_KEYS_TAB_CONTENT_CREDENTIAL;
    display: flex;
    flex-direction: column;
    gap: ${ORG_SETTINGS_SPACING.standardPadding}px;
  `,
  credentialHeader: css`
    label: API_KEYS_TAB_CONTENT_CREDENTIAL_HEADER;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${ORG_SETTINGS_SPACING.gutterX}px;
  `,
  status: css`
    label: API_KEYS_TAB_CONTENT_STATUS;
    display: inline-flex;
    padding: 4px 8px;
    border: 1px solid ${ORG_SETTINGS_COLORS.border};
    font: inherit;
    text-transform: capitalize;
  `,
  activeStatus: css`
    label: API_KEYS_TAB_CONTENT_ACTIVE_STATUS;
    border-color: ${ORG_SETTINGS_COLORS.green};
    color: ${ORG_SETTINGS_COLORS.green};
  `,
  revokedStatus: css`
    label: API_KEYS_TAB_CONTENT_REVOKED_STATUS;
    border-color: ${ORG_SETTINGS_COLORS.red};
    color: ${ORG_SETTINGS_COLORS.red};
  `,
  secondaryAction: css`
    ${ORG_SETTINGS_MEDIUM_ACTION_BUTTON_STYLE};
    label: API_KEYS_TAB_CONTENT_SECONDARY_ACTION;
    border: 1px solid ${ORG_SETTINGS_COLORS.border};
    background: ${ORG_SETTINGS_COLORS.white};
    color: ${ORG_SETTINGS_COLORS.text};

    &:hover {
      border-color: ${ORG_SETTINGS_COLORS.text};
    }

    &:disabled {
      border-color: ${ORG_SETTINGS_COLORS.disabledButtonBorder};
      color: ${ORG_SETTINGS_COLORS.textSoft};
      cursor: not-allowed;
    }
  `,
  dangerAction: css`
    ${ORG_SETTINGS_MEDIUM_ACTION_BUTTON_STYLE};
    label: API_KEYS_TAB_CONTENT_DANGER_ACTION;
    border: 1px solid ${ORG_SETTINGS_COLORS.red};
    background: ${ORG_SETTINGS_COLORS.white};
    color: ${ORG_SETTINGS_COLORS.red};

    &:hover {
      border-color: ${ORG_SETTINGS_COLORS.text};
    }

    &:disabled {
      border-color: ${ORG_SETTINGS_COLORS.disabledButtonBorder};
      color: ${ORG_SETTINGS_COLORS.textSoft};
      cursor: not-allowed;
    }
  `,
};
